import type { No } from '@mymaxx/flow-engine';
import {
  Clock,
  MapPin,
  GitBranch,
  Globe,
  Hourglass,
  ListChecks,
  MessageSquare,
  MessageSquareQuote,
  Play,
  Server,
  Share2,
  Square,
  Variable,
} from 'lucide-react';

export type TipoNo = No['tipo'];

/** Saídas de um nó: cada uma vira um ponto de conexão no canvas. */
export interface SaidaNo {
  /** Rótulo da aresta na definição (undefined = saída única). */
  id?: string;
  nome: string;
  cor?: string;
}

export interface DescricaoNo {
  tipo: TipoNo;
  nome: string;
  descricao: string;
  Icone: typeof MessageSquare;
  /** Cor da borda/cabeçalho no canvas. */
  cor: string;
  /** Aparece na paleta (o "início" não, porque já vem no fluxo). */
  naPaleta: boolean;
  /** Tem ponto de entrada? (o início não tem) */
  temEntrada: boolean;
  /** Saídas fixas; o menu calcula as dele a partir das opções. */
  saidas: SaidaNo[];
}

const VERDE = '#16a34a';
const VERMELHO = '#dc2626';

export const CATALOGO_NOS: Record<TipoNo, DescricaoNo> = {
  inicio: {
    tipo: 'inicio',
    nome: 'Início',
    descricao: 'Por onde a conversa começa.',
    Icone: Play,
    cor: '#16a34a',
    naPaleta: false,
    temEntrada: false,
    saidas: [{ nome: '' }],
  },
  mensagem: {
    tipo: 'mensagem',
    nome: 'Mensagem',
    descricao: 'Envia um texto e segue adiante.',
    Icone: MessageSquare,
    cor: '#1d5af0',
    naPaleta: true,
    temEntrada: true,
    saidas: [{ nome: '' }],
  },
  pergunta: {
    tipo: 'pergunta',
    nome: 'Pergunta',
    descricao: 'Pergunta algo e guarda a resposta numa variável.',
    Icone: MessageSquareQuote,
    cor: '#7c3aed',
    naPaleta: true,
    temEntrada: true,
    saidas: [{ nome: '' }],
  },
  menu: {
    tipo: 'menu',
    nome: 'Menu de opções',
    descricao: 'Lista opções numeradas; cada uma segue por um caminho.',
    Icone: ListChecks,
    cor: '#0891b2',
    naPaleta: true,
    temEntrada: true,
    saidas: [],
  },
  condicao: {
    tipo: 'condicao',
    nome: 'Condição',
    descricao: 'Compara uma variável e desvia o caminho.',
    Icone: GitBranch,
    cor: '#d97706',
    naPaleta: true,
    temEntrada: true,
    saidas: [
      { id: 'sim', nome: 'Sim', cor: VERDE },
      { id: 'nao', nome: 'Não', cor: VERMELHO },
    ],
  },
  acao_hubsoft: {
    tipo: 'acao_hubsoft',
    nome: 'Consulta no HubSoft',
    descricao: 'Busca dados do cliente no ERP (somente leitura).',
    Icone: Server,
    cor: '#0d9488',
    naPaleta: true,
    temEntrada: true,
    saidas: [
      { id: 'ok', nome: 'Encontrou', cor: VERDE },
      { id: 'erro', nome: 'Não encontrou', cor: VERMELHO },
    ],
  },
  horario: {
    tipo: 'horario',
    nome: 'Horário do setor',
    descricao: 'Verifica se o setor está atendendo agora.',
    Icone: Clock,
    cor: '#ca8a04',
    naPaleta: true,
    temEntrada: true,
    saidas: [
      { id: 'dentro', nome: 'Dentro', cor: VERDE },
      { id: 'fora', nome: 'Fora', cor: VERMELHO },
    ],
  },
  escolher_ponto: {
    tipo: 'escolher_ponto',
    nome: 'Escolher ponto',
    descricao: 'Pergunta para qual instalação é o atendimento. Com um ponto só, não pergunta nada.',
    Icone: MapPin,
    cor: '#7c3aed',
    naPaleta: true,
    temEntrada: true,
    saidas: [
      { id: undefined, nome: 'Escolheu', cor: VERDE },
      { id: 'nenhum', nome: 'Sem ponto', cor: VERMELHO },
    ],
  },
  transferir_setor: {
    tipo: 'transferir_setor',
    nome: 'Transferir para setor',
    descricao: 'Coloca o cliente na fila de um setor. Encerra o fluxo.',
    Icone: Share2,
    cor: '#2563eb',
    naPaleta: true,
    temEntrada: true,
    saidas: [],
  },
  transferir_fluxo: {
    tipo: 'transferir_fluxo',
    nome: 'Ir para outro fluxo',
    descricao: 'Passa a conversa para outro fluxo. Encerra este.',
    Icone: Share2,
    cor: '#4f46e5',
    naPaleta: true,
    temEntrada: true,
    saidas: [],
  },
  definir_variavel: {
    tipo: 'definir_variavel',
    nome: 'Definir variável',
    descricao: 'Guarda um valor para usar mais adiante.',
    Icone: Variable,
    cor: '#64748b',
    naPaleta: true,
    temEntrada: true,
    saidas: [{ nome: '' }],
  },
  aguardar: {
    tipo: 'aguardar',
    nome: 'Aguardar',
    descricao: 'Espera um tempo antes de continuar.',
    Icone: Hourglass,
    cor: '#9333ea',
    naPaleta: true,
    temEntrada: true,
    saidas: [{ nome: '' }],
  },
  webhook_http: {
    tipo: 'webhook_http',
    nome: 'Chamar sistema externo',
    descricao: 'Faz uma chamada HTTP e guarda a resposta.',
    Icone: Globe,
    cor: '#0369a1',
    naPaleta: true,
    temEntrada: true,
    saidas: [
      { id: 'ok', nome: 'Sucesso', cor: VERDE },
      { id: 'erro', nome: 'Falha', cor: VERMELHO },
    ],
  },
  encerrar: {
    tipo: 'encerrar',
    nome: 'Encerrar',
    descricao: 'Termina o atendimento.',
    Icone: Square,
    cor: '#dc2626',
    naPaleta: true,
    temEntrada: true,
    saidas: [],
  },
};

export const NOS_DA_PALETA = Object.values(CATALOGO_NOS).filter((n) => n.naPaleta);

/** Saídas reais de um nó — o menu depende das opções configuradas. */
export function saidasDoNo(no: No): SaidaNo[] {
  if (no.tipo === 'menu') {
    return no.opcoes.map((o) => ({ id: o.id, nome: o.rotulo || o.id }));
  }
  // A pergunta tem a saída normal (resposta válida), uma para cada palavra-chave e,
  // quando há limite de tentativas, a saída de quem não conseguiu responder.
  if (no.tipo === 'pergunta' && (no.atalhos.length > 0 || no.maxTentativas)) {
    return [
      { id: undefined, nome: 'Respondeu' },
      ...no.atalhos.map((a) => ({ id: a.id, nome: a.rotulo || a.id })),
      ...(no.maxTentativas
        ? [{ id: 'desistiu', nome: 'Não conseguiu responder', cor: VERMELHO }]
        : []),
    ];
  }
  return CATALOGO_NOS[no.tipo].saidas;
}

/** Texto curto mostrado dentro do nó no canvas. */
export function resumoDoNo(no: No): string {
  switch (no.tipo) {
    case 'inicio':
      return 'A conversa começa aqui';
    case 'mensagem':
      return no.texto;
    case 'pergunta':
      return `${no.texto} → {{${no.variavel}}}`;
    case 'menu':
      return `${no.texto} (${no.opcoes.length} opções)`;
    case 'condicao':
      return `${no.variavel} ${no.operador} ${no.valor ?? ''}`;
    case 'acao_hubsoft':
      return `${no.acao} → {{${no.variavelResultado}}}`;
    case 'horario':
      return 'Verifica o horário do setor';
    case 'escolher_ponto':
      return no.texto;
    case 'transferir_setor':
      return 'Envia para a fila do setor';
    case 'transferir_fluxo':
      return 'Continua em outro fluxo';
    case 'definir_variavel':
      return `{{${no.variavel}}} = ${no.valor}`;
    case 'aguardar':
      return `Espera ${no.segundos}s`;
    case 'webhook_http':
      return `${no.metodo} ${no.url}`;
    case 'encerrar':
      return no.mensagem ?? 'Fim do atendimento';
  }
}

/** Nó recém-arrastado da paleta, com valores que já fazem sentido. */
export function noPadrao(tipo: TipoNo, id: string, posicao: { x: number; y: number }): No {
  const base = { id, posicao };
  switch (tipo) {
    case 'inicio':
      return { ...base, tipo: 'inicio' };
    case 'mensagem':
      return { ...base, tipo: 'mensagem', texto: 'Escreva aqui a mensagem…' };
    case 'pergunta':
      return {
        ...base,
        tipo: 'pergunta',
        texto: 'O que deseja perguntar?',
        variavel: 'resposta',
        atalhos: [],
        validacao: 'texto',
      };
    case 'menu':
      return {
        ...base,
        tipo: 'menu',
        texto: 'Como posso ajudar?',
        opcoes: [
          { id: 'opcao1', rotulo: 'Primeira opção', sinonimos: [] },
          { id: 'opcao2', rotulo: 'Segunda opção', sinonimos: [] },
        ],
      };
    case 'condicao':
      return { ...base, tipo: 'condicao', variavel: 'resposta', operador: 'igual', valor: '' };
    case 'acao_hubsoft':
      return {
        ...base,
        tipo: 'acao_hubsoft',
        acao: 'buscar_pontos_por_documento',
        parametros: { documento: '{{documento}}' },
        variavelResultado: 'cliente',
      };
    case 'horario':
      return { ...base, tipo: 'horario', setorId: '' };
    case 'escolher_ponto':
      return {
        ...base,
        tipo: 'escolher_ponto',
        texto: 'Para qual ponto é o atendimento?',
        variavel: 'cliente.pontos',
      };
    case 'transferir_setor':
      return { ...base, tipo: 'transferir_setor', setorId: '' };
    case 'transferir_fluxo':
      return { ...base, tipo: 'transferir_fluxo', fluxoId: '' };
    case 'definir_variavel':
      return { ...base, tipo: 'definir_variavel', variavel: 'variavel', valor: '' };
    case 'aguardar':
      return { ...base, tipo: 'aguardar', segundos: 60 };
    case 'webhook_http':
      return {
        ...base,
        tipo: 'webhook_http',
        url: 'https://',
        metodo: 'POST',
        corpo: {},
        variavelResultado: 'retorno',
      };
    case 'encerrar':
      return { ...base, tipo: 'encerrar', mensagem: 'Obrigado pelo contato!' };
  }
}
